import { MutationBatcher } from 'components/framework/MutationBatcher';
import * as Models from 'components/framework/models';
import {
  type SmartZoneFormData,
  type SmartZoneField,
  type UpdateRecords,
} from '../../../../javascripts/shared-types';
import { QueryBatcher } from '../../../framework/QueryBatcher';
import { type GetWorkflow } from './smartZoneFormData';

// Constants for batch configuration
const GQL_BATCH_TIMEOUT = 200;
const GQL_BATCH_LIMIT = 20;

// Map of record types to model classes
const MODEL_MAP = {
  features: Models.Feature,
  requirements: Models.Requirement,
  epics: Models.Epic,
  releases: Models.Release,
  initiatives: Models.Initiative,
  goals: Models.Goal,
  ideas: Models.Idea,
  tasks: Models.Task,
  pages: Models.Page,
  pdlcSteps: Models.PdlcStep,
};

const WORKFLOW_FIELD_MAP = {
  features: 'featureWorkflow',
  requirements: 'requirementWorkflow',
  epics: 'epicWorkflow',
  releases: 'releaseWorkflow',
  initiatives: 'initiativeWorkflow',
  goals: 'strategicImperativeWorkflow',
  ideas: 'ideaWorkflow',
} as const;

const TEAM_WORKFLOW_FIELD_MAP = {
  features: 'featureWorkflow',
  epics: 'epicWorkflow',
} as const;

type TransformedAttribute = { id: unknown } | { id: unknown }[];

const transformAttribute = (
  name: string,
  value: unknown | unknown[]
): TransformedAttribute =>
  Array.isArray(value) ? value.map(id => ({ id })) : { id: value };

const transformAttributes = (
  attributes: Record<string, unknown>,
  fields: SmartZoneField[]
): Record<string, TransformedAttribute> =>
  Object.fromEntries(
    Object.entries(attributes)
      // omit invalid attributes and any with no value - note this means smart zones can't unset values
      .filter(
        ([name, value]) => fields.some(field => field.name === name) && !!value
      )
      .map(([name, value]) => [name, transformAttribute(name, value)])
  );

/** Retrieves applicable workflowId for each record provided, for primary workflow and team workflow if applicable */
const getRecordWorkflows = async (
  records: { recordType: string; recordId: string }[]
) => {
  const batcher = new QueryBatcher();

  const results = (await batcher.execute(batcher =>
    records.map(async ({ recordType, recordId }) => {
      const ModelClass = MODEL_MAP[recordType];
      const workflowField = WORKFLOW_FIELD_MAP[recordType];
      const teamWorkflowField = TEAM_WORKFLOW_FIELD_MAP[recordType];
      return batcher.find(
        ModelClass.select('id', 'name').merge({
          project: [workflowField + 'Id'],
          ...(teamWorkflowField ? { team: [teamWorkflowField + 'Id'] } : {}),
        }),
        recordId
      );
    })
  )) as any[];

  return Object.fromEntries(
    records.map(({ recordId, recordType }, i) => {
      const workflowField = WORKFLOW_FIELD_MAP[recordType] + 'Id';
      const teamWorkflowField = TEAM_WORKFLOW_FIELD_MAP[recordType] + 'Id';
      return [
        recordId,
        {
          workflowId: results[i].project[workflowField],
          teamWorkflowId:
            teamWorkflowField && results[i].team
              ? results[i].team?.[teamWorkflowField]
              : undefined,
        },
      ];
    })
  );
};

type GetRecordUpdater = (
  smartZoneFormData: SmartZoneFormData,
  helpers: { getWorkflow: GetWorkflow; getTeamWorkflow: GetWorkflow }
) => UpdateRecords;

/**
 * Updates multiple records with their respective attributes
 *
 * @param records - Array of objects containing record type, ID, and attributes to update
 * @returns Promise that resolves with the results of the updates or rejects with errors
 */
export const getRecordUpdater: GetRecordUpdater =
  (smartZoneFormData, helpers) => async records => {
    // Create a batcher to optimize API calls
    const batcher = new MutationBatcher({
      timeout: GQL_BATCH_TIMEOUT,
      limit: GQL_BATCH_LIMIT,
      reactivelyUpdate: true,
    });

    const hasStatusUpdate = records.filter(({ attributes }) =>
      Object.keys(attributes).some(
        k => k === 'workflowStatus' || k === 'teamWorkflowStatus'
      )
    );

    // if any record updates include workflowStatus, we need to only apply those if they use the same
    // workflow as the project. We retrieve the workflowId for each record here if needed
    const workflowIdMap = hasStatusUpdate.length
      ? await getRecordWorkflows(hasStatusUpdate)
      : {};

    const warningFlags = {
      workflowStatus: false,
      teamWorkflowStatus: false,
    };

    // Track results for each record update
    const updatePromises = records.map(async ({ attributes, ...recordKey }) => {
      const { recordType, recordId } = recordKey;

      // Get the model class for this record type
      const ModelClass = MODEL_MAP[recordType];
      const fields = smartZoneFormData.find(
        r => r.recordType === recordType
      )?.fields;

      if (!ModelClass || !fields) {
        return {
          ...recordKey,
          success: false,
          errors: [`Unsupported record type: ${recordType}`],
        };
      }

      const record = new ModelClass({ id: recordId });

      // Check if the record's workflow matches the project's workflow
      // skip any updates to workflowStatus if the workflow doesn't match
      const workflowName = WORKFLOW_FIELD_MAP[recordType];
      const projectWorkflow = await helpers.getWorkflow(workflowName);
      const allowStatus =
        workflowIdMap[recordId]?.workflowId === projectWorkflow?.id;

      let allowTeamStatus: boolean;
      if (attributes.teamWorkflowStatus) {
        // if setting team, assume the team status is valid
        allowTeamStatus = true;
      } else {
        // otherwise, check against the default team workflow
        const teamWorkflow = await helpers.getTeamWorkflow(workflowName);
        allowTeamStatus =
          workflowIdMap[recordId]?.teamWorkflowId === teamWorkflow?.id;
      }

      // omit status field definitions if they're not allowed
      const allowedFields = fields.filter(
        f =>
          (allowStatus || f.name !== 'workflowStatus') &&
          (allowTeamStatus || f.name !== 'teamWorkflowStatus')
      );

      warningFlags.workflowStatus ||= !!(
        attributes.workflowStatus && !allowStatus
      );
      warningFlags.teamWorkflowStatus ||= !!(
        attributes.teamWorkflowStatus && !allowTeamStatus
      );

      const transformed = transformAttributes(attributes, allowedFields);

      if (!Object.keys(transformed).length) {
        return {
          ...recordKey,
          success: false,
          errors: ['No valid attributes to update'],
        };
      }

      try {
        const success = await record.update(transformed, { batcher });

        return {
          ...recordKey,
          success,
          errors: record.errors ? record.errors.fullMessages : undefined,
        };
      } catch (error) {
        return {
          ...recordKey,
          success: false,
          errors: [error],
        };
      }
    });

    // Wait for all updates to complete
    const results = await Promise.all(updatePromises);

    // Determine overall success (true if all updates succeeded)
    const success = results.every(result => result.success);

    const warnings: string[] = [];
    if (warningFlags.workflowStatus) {
      warnings.push(
        "Could not update status for some records because it doesn't match the project workflow"
      );
    }
    if (warningFlags.teamWorkflowStatus) {
      warnings.push(
        "Could not update team status for some records because it doesn't match the team workflow"
      );
    }

    return {
      success,
      results,
      warnings,
    };
  };
